import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import CameraCapture from '../components/CameraCapture';
import RecipeEditor from './RecipeEditor';
import {
  IconPhoto, IconLinkAction, IconNavCamera, IconUploadAction,
  IconCheck, IconBack, IconRefreshAction, IconLoader,
} from '../components/icons';
import './Import.css';
import './demo/DemoFeatures.css';

/* ── Fortschrittsanzeige ── */
function ImportProgress({ phase, onNavigate }) {
  const steps = ['Methode', 'Import', 'Rechte', 'Prüfen'];
  return (
    <div className="import-progress">
      {steps.map((label, i) => {
        const num = i + 1;
        const isDone   = phase > num;
        const isActive = phase === num;
        return (
          <React.Fragment key={num}>
            {i > 0 && <div className={`import-step-line${isDone ? ' done' : ''}`} />}
            <div
              className={`import-step${isActive ? ' active' : ''}${isDone ? ' done clickable' : ''}`}
              onClick={isDone ? () => onNavigate(num) : undefined}
              title={isDone ? label : undefined}
            >
              <div className="import-step-num">{isDone ? '✓' : num}</div>
              <div className="import-step-label">{label}</div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function getOsHint() {
  if ('ontouchstart' in window) return null;
  const ua = navigator.userAgent;
  if (/Win/.test(ua)) return 'Win + Umschalt + S → Bereich markieren → dann Strg+V';
  if (/Mac/.test(ua)) return 'Cmd + Shift + 4 → Bereich markieren → dann ⌘V';
  return 'Screenshot machen → dann Strg+V oder ⌘V';
}

/* ── Hauptkomponente ── */
export default function Import() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [phase, setPhase]   = useState(1);
  const [method, setMethod] = useState(null); // 'foto' | 'url' | 'kamera'

  // Phase 2
  const [files, setFiles]                   = useState([]);
  const [filePreviews, setFilePreviews]     = useState([]);
  const [runningProgress, setRunningProgress] = useState('');
  const [importUrl, setImportUrl]     = useState('');
  const [running, setRunning]         = useState(false);
  const [error, setError]             = useState('');
  const [errorCode, setErrorCode]     = useState('');
  // recipe = bereits in DB (Foto-Import) | recipeData = nur geparst, noch nicht gespeichert (URL-Import)
  const [recipe, setRecipe]           = useState(null);
  const [recipeData, setRecipeData]   = useState(null);
  const [showCamera, setShowCamera]   = useState(false);
  const [quota, setQuota]             = useState(null);
  const fileInputRef = useRef(null);

  // Phase 3
  const [source, setSource]               = useState('');
  const [imageMode, setImageMode]         = useState(null); // 'file' | 'url'
  const [imageFile, setImageFile]         = useState(null);
  const [imageFilePreview, setImageFilePreview] = useState(null);
  const [coverUrl, setCoverUrl]           = useState('');
  const [coverPreviewOk, setCoverPreviewOk] = useState(false);
  const [imageRights, setImageRights]     = useState(false);
  const [sourceRights, setSourceRights]   = useState(false);
  const [phase3Saving, setPhase3Saving]   = useState(false);
  const [phase3Error, setPhase3Error]     = useState('');
  // Titelbild-URL für URL-Import: erst nach Speichern im Editor hochladen
  const [pendingCoverUrl, setPendingCoverUrl] = useState('');
  // Demo-State für Gast-Ansicht
  const [demoImportPhase, setDemoImportPhase] = useState('idle'); // idle | loading | done

  useEffect(() => {
    if (!user) return;
    api.getUploadQuota().then(setQuota).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Strg+V / ⌘V Screenshot einfügen (nur wenn Foto-Phase aktiv)
  useEffect(() => {
    if (phase !== 2 || (method !== 'foto' && method !== 'kamera')) return;
    function handlePaste(e) {
      const tag = e.target?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of [...items]) {
        if (item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) {
            setFiles(prev => prev.length < 10 ? [...prev, f] : prev);
            setFilePreviews(prev => prev.length < 10 ? [...prev, URL.createObjectURL(f)] : prev);
            setError(''); setErrorCode('');
          }
          break;
        }
      }
    }
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [phase, method]);

  /* ── Phase 2: Foto scannen (Multi-Bild) ── */
  async function handleFotoScan() {
    if (!files.length) return;
    setRunning(true);
    setError('');
    setRunningProgress('');
    try {
      let primaryFilename = null;
      let mergedBox = null;

      for (let i = 0; i < files.length; i++) {
        setRunningProgress(files.length > 1 ? `Bild ${i + 1} von ${files.length} wird analysiert…` : '');
        const data = await api.uploadImage(files[i], '', true);
        const boxes = data.boundingBoxes || [];
        if (!boxes.length) continue;
        const box = boxes[0];

        if (mergedBox === null) {
          primaryFilename = data.filename;
          mergedBox = { ...box };
          if (!Array.isArray(mergedBox.ingredients)) mergedBox.ingredients = [];
          if (!Array.isArray(mergedBox.steps)) mergedBox.steps = [];
          if (typeof mergedBox.text !== 'string') mergedBox.text = '';
        } else {
          // Zutaten + Schritte aus weiteren Bildern anhängen
          if (Array.isArray(box.ingredients)) mergedBox.ingredients.push(...box.ingredients);
          if (Array.isArray(box.steps)) mergedBox.steps.push(...box.steps);
          if (typeof box.text === 'string') mergedBox.text += '\n' + box.text;
        }
      }

      if (!mergedBox || !primaryFilename) {
        setError('Kein Rezept erkannt. Bitte klarere Bilder wählen.');
        setRunning(false);
        return;
      }

      setRunningProgress('Rezept wird erstellt…');
      const result = await api.processBox(primaryFilename, mergedBox, mergedBox.title || '', false);
      if (!result.recipe) {
        setError('Das Rezept konnte nicht verarbeitet werden. Bitte versuche es erneut.');
        setRunning(false);
        return;
      }
      setRecipe(result.recipe);
      setSource('');
      setPhase(3);
    } catch (err) {
      setError(err.message || 'Beim Scannen ist ein Fehler aufgetreten.');
    }
    setRunning(false);
    setRunningProgress('');
  }

  function removeFile(i) {
    setFiles(prev => prev.filter((_, idx) => idx !== i));
    setFilePreviews(prev => prev.filter((_, idx) => idx !== i));
  }

  function addFiles(selected) {
    const newFiles = [...selected].slice(0, 10);
    setFiles(prev => {
      const combined = [...prev, ...newFiles];
      return combined.slice(0, 10);
    });
    setFilePreviews(prev => {
      const newPreviews = newFiles.map(f => URL.createObjectURL(f));
      return [...prev, ...newPreviews].slice(0, 10);
    });
    setError('');
  }

  /* ── Phase 2: URL importieren ── */
  async function handleUrlImport() {
    const trimmed = importUrl.trim();
    if (!trimmed) return;
    setRunning(true);
    setError('');
    try {
      let domain = '';
      try { domain = new URL(trimmed).hostname.replace('www.', ''); } catch {}
      const data = await api.urlImport(trimmed, domain);
      if (!data.recipe && !data.recipe_data) {
        setError('Kein Rezept gefunden. Bitte eine andere URL versuchen.');
        setRunning(false);
        return;
      }
      if (data.recipe_data) {
        // Noch nicht gespeichert — erst nach Phase-3-Bestätigung
        setRecipeData(data.recipe_data);
        setRecipe(null);
      } else {
        // Duplikat: bereits in DB vorhanden
        setRecipe(data.recipe);
        setRecipeData(null);
      }
      setSource(domain);
      if (data.suggested_cover_url) {
        setCoverUrl(data.suggested_cover_url);
        setImageMode('url');
        setImageRights(false);
        setCoverPreviewOk(false);
      }
      setPhase(3);
    } catch (err) {
      setError(err.message || 'Die URL konnte nicht importiert werden.');
      setErrorCode(err.code || '');
    }
    setRunning(false);
  }

  /* ── Phase 2: Kamera ── */
  function handleCameraCapture(capturedFile) {
    setShowCamera(false);
    addFiles([capturedFile]);
  }

  /* ── Phase 3: Rechte & Bild speichern ── */
  async function handlePhase3Continue() {
    if (!sourceRights) return;
    setPhase3Saving(true);
    setPhase3Error('');
    try {
      if (recipeData) {
        // URL-Import: Rezept noch NICHT erstellen — erst wenn Nutzer im Editor auf Speichern klickt
        // Quelle in recipeData mergen
        const pendingCover = (imageMode === 'url' && coverUrl && imageRights) ? coverUrl : '';
        setPendingCoverUrl(pendingCover);
        setRecipeData(prev => ({
          ...prev,
          source_file: source || prev.source_file || '',
          image_url: pendingCover || '',
        }));
        setPhase(4);
      } else {
        // Foto-Import: Rezept ist bereits in DB — Quelle + Cover aktualisieren
        if (source && recipe) {
          await api.updateRecipe(recipe.id, { source_file: source });
        }
        if (imageMode === 'file' && imageFile && recipe) {
          await api.uploadRecipeCover(recipe.id, imageFile);
        }
        if (imageMode === 'url' && coverUrl && imageRights && recipe) {
          await api.importRecipeCoverFromUrl(recipe.id, coverUrl);
        }
        setPhase(4);
      }
    } catch (err) {
      setPhase3Error(err.message || 'Fehler beim Speichern. Bitte erneut versuchen.');
    }
    setPhase3Saving(false);
  }

  /* ── Phase 4: Verwerfen ── */
  async function handleDiscard() {
    if (recipe?.id) {
      try { await api.deleteRecipe(recipe.id); } catch {}
    }
    navigate('/my-recipes');
  }

  /* ── Phase 4: Nach dem Speichern → Live-Ansicht ── */
  async function handleSaved(savedRecipe) {
    const id = savedRecipe?.id || recipe?.id;
    if (id && pendingCoverUrl) {
      try { await api.importRecipeCoverFromUrl(id, pendingCoverUrl); } catch {}
      setPendingCoverUrl('');
    }
    if (id) navigate(`/recipe/${id}`);
  }

  /* ── Zurück-Navigation ── */
  function goBack() {
    if (phase === 2) { setPhase(1); setMethod(null); setFiles([]); setFilePreviews([]); setImportUrl(''); setError(''); setErrorCode(''); setRecipeData(null); }
    else if (phase === 3) { setPhase(2); setError(''); setErrorCode(''); }
  }

  /* ── Klick auf abgeschlossenen Schritt ── */
  function navigateToPhase(target) {
    if (target >= phase) return; // nur rückwärts
    if (target === 1) { setPhase(1); setMethod(null); setFiles([]); setFilePreviews([]); setImportUrl(''); setError(''); setPhase3Error(''); setRecipeData(null); setRecipe(null); }
    else if (target === 2) { setPhase(2); setError(''); setPhase3Error(''); }
    else if (target === 3) { setPhase(3); setPhase3Error(''); }
  }

  /* ════════════════════════════════════════
     RENDER
  ════════════════════════════════════════ */
  const DEMO_RESULT = {
    title: 'Spaghetti Carbonara',
    category: 'Pasta & Nudeln',
    prep_time: 20,
    servings: 4,
    ingredients: [
      { name: 'Spaghetti', amount: '400', unit: 'g' },
      { name: 'Guanciale (oder Speck)', amount: '150', unit: 'g' },
      { name: 'Eier', amount: '3', unit: 'Stk.' },
      { name: 'Pecorino Romano', amount: '80', unit: 'g' },
      { name: 'Schwarzer Pfeffer', amount: '', unit: 'nach Geschmack' },
      { name: 'Salz', amount: '', unit: 'nach Geschmack' },
    ],
    allergens: ['Gluten', 'Eier', 'Milch'],
  };

  if (!user) return (
    <div className="demo-feature-page" style={{ padding: '0 16px' }}>
      <div className="demo-feature-hero">
        <div className="demo-feature-icon">🔗</div>
        <h1 className="demo-feature-title">Rezept-Import</h1>
        <p className="demo-feature-sub">
          URL aus dem Browser kopieren, einfügen — fertig. Strixly extrahiert Zutaten,
          Mengen und Schritte aus jeder Rezept-Webseite. Kein Abtippen mehr.
        </p>
      </div>

      <div className="demo-steps">
        <div className="demo-step">
          <div className="demo-step-num">1</div>
          <div className="demo-step-body">
            <div className="demo-step-label">URL kopieren</div>
            <div className="demo-step-desc">Jede Rezept-Webseite funktioniert — Chefkoch, Kochbar, Jamie Oliver, BBC Food und tausende mehr.</div>
          </div>
        </div>
        <div className="demo-step">
          <div className="demo-step-num">2</div>
          <div className="demo-step-body">
            <div className="demo-step-label">KI liest die Seite</div>
            <div className="demo-step-desc">Strixly öffnet die Seite im Hintergrund, extrahiert das Rezept und strukturiert Zutaten, Mengen und Zubereitungsschritte.</div>
          </div>
        </div>
        <div className="demo-step">
          <div className="demo-step-num">3</div>
          <div className="demo-step-body">
            <div className="demo-step-label">In Sammlung speichern</div>
            <div className="demo-step-desc">Das importierte Rezept landet direkt in deiner persönlichen Sammlung — mit Allergenen, Portionsrechner und Einkaufsliste.</div>
          </div>
        </div>
      </div>

      <div className="demo-mockup">
        <div className="demo-mockup-bar">
          <div className="demo-mockup-dot" style={{background:'#ff5f57'}}/>
          <div className="demo-mockup-dot" style={{background:'#ffbd2e'}}/>
          <div className="demo-mockup-dot" style={{background:'#28c840'}}/>
          <span style={{marginLeft:8}}>Rezept-Import — Demo</span>
        </div>
        <div className="demo-mockup-body">
          <div className="demo-url-bar">
            <div className="demo-url-input">https://www.chefkoch.de/rezepte/spaghetti-carbonara-original…</div>
            {demoImportPhase === 'idle' && (
              <button onClick={() => { setDemoImportPhase('loading'); setTimeout(() => setDemoImportPhase('done'), 2000); }} style={{
                background:'var(--green, #2da86a)', color:'#fff',
                border:'none', borderRadius:6, padding:'6px 14px',
                fontSize:'0.8rem', fontWeight:700, cursor:'pointer', flexShrink:0,
              }}>Importieren</button>
            )}
            {demoImportPhase === 'loading' && <span className="demo-url-badge">🔍 Lädt…</span>}
            {demoImportPhase === 'done'    && <span className="demo-url-badge">✓ Fertig</span>}
          </div>

          {demoImportPhase === 'idle' && (
            <div style={{textAlign:'center',padding:'20px 0',color:'var(--txt3, #888)',fontSize:'0.88rem'}}>
              Klick auf "Importieren" um das Ergebnis zu sehen
            </div>
          )}

          {demoImportPhase === 'loading' && (
            <div style={{textAlign:'center',padding:'32px 0'}}>
              <div className="loader" style={{margin:'0 auto 16px'}}/>
              <p style={{color:'var(--txt3, #888)',fontSize:'0.88rem'}}>Strixly liest die Seite und extrahiert das Rezept…</p>
            </div>
          )}

          {demoImportPhase === 'done' && (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16,flexWrap:'wrap',gap:8}}>
                <div>
                  <div style={{fontWeight:800,fontSize:'1.1rem',color:'var(--txt, #1a1a1a)'}}>
                    🍝 {DEMO_RESULT.title}
                  </div>
                  <div style={{fontSize:'0.8rem',color:'var(--txt3, #888)',marginTop:4}}>
                    {DEMO_RESULT.category} · ⏱ {DEMO_RESULT.prep_time} Min. · 🍽 {DEMO_RESULT.servings} Portionen
                  </div>
                </div>
                <span style={{
                  background:'var(--green-muted)',color:'var(--green, #2da86a)',
                  border:'1px solid var(--green, #2da86a)',borderRadius:20,
                  padding:'3px 12px',fontSize:'0.75rem',fontWeight:700,
                }}>✓ Importiert</span>
              </div>

              <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--txt3, #888)',marginBottom:8}}>
                ZUTATEN ({DEMO_RESULT.ingredients.length} erkannt)
              </div>
              <div className="demo-ocr-result" style={{marginBottom:16}}>
                {DEMO_RESULT.ingredients.map(ing => (
                  <div key={ing.name} className="demo-ocr-ing">
                    <span>{ing.name}</span>
                    <span className="demo-ocr-ing-amt">
                      {ing.amount ? `${ing.amount} ${ing.unit}` : ing.unit}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--txt3, #888)',marginBottom:8}}>
                ALLERGENE
              </div>
              <div className="demo-allergen-row">
                {DEMO_RESULT.allergens.map(a => (
                  <span key={a} className="demo-allergen-chip warn">⚠️ {a}</span>
                ))}
                <span className="demo-allergen-chip ok">✓ Nussfrei</span>
                <span className="demo-allergen-chip ok">✓ Sojafrei</span>
              </div>
              <div style={{marginTop:16,fontSize:'0.8rem',color:'var(--txt3, #888)'}}>
                4 Zubereitungsschritte extrahiert · Portionsrechner aktiv
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="demo-cta-box">
        <h3>Beliebige Rezepte importieren</h3>
        <p>Schluss mit Lesezeichen-Chaos. Alle Rezepte an einem Ort —<br/>strukturiert, mit Allergenen und Einkaufsliste.</p>
        <a href="/login?tab=register" className="demo-cta-btn">Kostenlos starten →</a>
        <div className="demo-cta-microcopy">Keine Kreditkarte · DSGVO-konform · Server in Österreich/EU</div>
      </div>
    </div>
  );

  return (
    <div className="import-page fade-in">
      {showCamera && (
        <CameraCapture onCapture={handleCameraCapture} onClose={() => setShowCamera(false)} />
      )}

      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Rezept <em>importieren</em></div>
          <div className="page-sub">Foto, Screenshot oder Link — Strixly erkennt dein Rezept automatisch</div>
        </div>
        {quota && !quota.unlimited && (
          <div className="import-quota-badge">
            <span>{quota.remaining} von {quota.limit} Scans übrig</span>
          </div>
        )}
      </div>

      <ImportProgress phase={phase} onNavigate={navigateToPhase} />

      {/* ── PHASE 1: Methode wählen ── */}
      {phase === 1 && (
        <div className="import-method-grid">
          <button className="import-method-card" onClick={() => { setMethod('foto'); setPhase(2); }}>
            <div className="import-method-icon">
              <IconPhoto size={32} />
            </div>
            <div className="import-method-title">Foto / Screenshot</div>
            <div className="import-method-sub">Foto eines gedruckten Rezepts, Screenshot aus einer App (Chefkoch, Pinterest, Yummly…)</div>
            <div className="import-method-arrow">→</div>
          </button>

          <button className="import-method-card" onClick={() => { setMethod('url'); setPhase(2); }}>
            <div className="import-method-icon">
              <IconLinkAction size={32} />
            </div>
            <div className="import-method-title">Link aus dem Web</div>
            <div className="import-method-sub">URL einer Rezeptseite eingeben — Strixly liest das Rezept automatisch aus</div>
            <div className="import-method-arrow">→</div>
          </button>

          <button className="import-method-card" onClick={() => { setMethod('kamera'); setPhase(2); }}>
            <div className="import-method-icon">
              <IconNavCamera size={32} />
            </div>
            <div className="import-method-title">Kamera (live)</div>
            <div className="import-method-sub">Kochbuch oder Rezeptkarte direkt mit der Kamera abfotografieren</div>
            <div className="import-method-arrow">→</div>
          </button>
        </div>
      )}

      {/* ── PHASE 2: Import ── */}
      {phase === 2 && (
        <div className="import-phase-wrap">
          <button className="import-back-btn" onClick={goBack}><IconBack size={14} /> Zurück</button>

          {/* FOTO */}
          {method === 'foto' && (
            <div className="import-foto-area">
              <input
                type="file" accept="image/*" multiple style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
              />

              {files.length === 0 ? (
                <label className="import-drop-zone" onClick={() => fileInputRef.current?.click()}>
                  <div className="import-drop-icon">
                    <IconUploadAction size={32} />
                  </div>
                  <div className="import-drop-title">Bilder auswählen</div>
                  <div className="import-drop-sub">JPG, PNG, WEBP bis 10 MB · bis zu 10 Bilder · Strg+V für Screenshot</div>
                </label>
              ) : (
                <div className="import-multi-preview">
                  {filePreviews.map((src, i) => (
                    <div key={i} className="import-multi-thumb">
                      <img src={src} alt={`Bild ${i + 1}`} />
                      <button
                        type="button"
                        className="import-multi-thumb-remove"
                        onClick={() => removeFile(i)}
                        title="Entfernen"
                      >×</button>
                      <div className="import-multi-thumb-num">{i + 1}</div>
                    </div>
                  ))}
                  {files.length < 10 && (
                    <button
                      type="button"
                      className="import-multi-add"
                      onClick={() => fileInputRef.current?.click()}
                      title="Weiteres Bild hinzufügen"
                    >
                      <span>+</span>
                      <span className="import-multi-add-sub">Bild<br/>hinzufügen</span>
                    </button>
                  )}
                </div>
              )}

              {files.length > 0 && (
                <div className="import-multi-hint">
                  {files.length === 1
                    ? 'Hast du mehrere Seiten? Füge bis zu 10 Bilder hinzu — Strixly fügt sie automatisch zusammen.'
                    : `${files.length} Bilder ausgewählt · Reihenfolge: von oben-links nach unten-rechts`}
                </div>
              )}

              {error && <div className="import-error">{error}</div>}

              {files.length > 0 && (
                <button
                  className="btn btn-primary import-btn-full"
                  onClick={handleFotoScan}
                  disabled={running}
                >
                  {running
                    ? (runningProgress || <><IconLoader size={14} /> Wird gescannt…</>)
                    : `Rezept erkennen${files.length > 1 ? ` (${files.length} Bilder)` : ''} →`}
                </button>
              )}

              {files.length === 0 && (
                <div className="import-divider"><span>oder</span></div>
              )}
              {files.length === 0 && (
                <button
                  className="import-camera-btn"
                  onClick={() => setShowCamera(true)}
                >
                  <IconNavCamera size={18} />
                  Kamera öffnen
                </button>
              )}

              {running && (
                <p className="import-running-hint">🤖 KI analysiert deine Bilder · DSGVO-konform · EU-Server</p>
              )}
            </div>
          )}

          {/* URL */}
          {method === 'url' && (
            <div className="import-url-area">
              <div className="import-url-field-wrap">
                <IconLinkAction className="import-url-icon" size={18} />
                <input
                  className="import-url-input"
                  type="url"
                  placeholder="https://www.chefkoch.de/rezepte/..."
                  value={importUrl}
                  onChange={e => { setImportUrl(e.target.value); setError(''); }}
                  onKeyDown={e => { if (e.key === 'Enter' && !running) handleUrlImport(); }}
                  autoFocus
                />
              </div>

              <div className="import-url-hints">
                <span className="import-url-hint-chip">chefkoch.de</span>
                <span className="import-url-hint-chip">allerhande.de</span>
                <span className="import-url-hint-chip">lecker.de</span>
                <span className="import-url-hint-chip">rezeptwelt.at</span>
                <span className="import-url-hint-chip">und viele mehr…</span>
              </div>

              {error && errorCode !== 'SPA_PAGE' && (
                <div className="import-error">{error}</div>
              )}
              {error && errorCode === 'SPA_PAGE' && (
                <div className="import-spa-hint">
                  <p className="import-spa-hint-text">
                    Diese Seite kann nicht direkt importiert werden — manche Seiten erlauben keinen automatischen Zugriff.
                  </p>
                  <div className="import-spa-options">
                    <div className="import-spa-option">
                      <div className="import-spa-option-head">Screenshot importieren</div>
                      {getOsHint() && (
                        <div className="import-spa-option-desc">{getOsHint()}</div>
                      )}
                      <button
                        className="import-spa-hint-btn"
                        onClick={() => { setMethod('foto'); setError(''); setErrorCode(''); setImportUrl(''); }}
                      >
                        Zum Foto-Import wechseln →
                      </button>
                    </div>
                    <div className="import-spa-divider" />
                    <div className="import-spa-option">
                      <div className="import-spa-option-head">Langes Rezept?</div>
                      <div className="import-spa-option-desc">
                        Mach 2–3 Screenshots und lade sie zusammen hoch — Strixly fügt sie automatisch zusammen.
                      </div>
                      <button
                        className="import-spa-hint-btn import-spa-hint-btn--ghost"
                        onClick={() => navigate('/upload')}
                      >
                        Zur Scan-Seite →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <button
                className="btn btn-primary import-btn-full"
                onClick={handleUrlImport}
                disabled={running || !importUrl.trim()}
              >
                {running ? <><IconLoader size={14} /> Wird importiert…</> : 'Rezept importieren →'}
              </button>

              {running && (
                <p className="import-running-hint">🤖 KI liest das Rezept aus · DSGVO-konform · EU-Server</p>
              )}
            </div>
          )}

          {/* KAMERA */}
          {method === 'kamera' && (
            <div className="import-kamera-area">
              <p className="import-kamera-hint">Halte dein Rezept gut beleuchtet vor die Kamera. Mehrere Aufnahmen werden automatisch zusammengefügt.</p>
              <button className="import-camera-btn import-camera-btn--big" onClick={() => setShowCamera(true)}>
                <IconNavCamera size={20} />
                {files.length === 0 ? 'Kamera öffnen' : 'Weitere Aufnahme'}
              </button>

              {files.length > 0 && (
                <div className="import-multi-preview">
                  {filePreviews.map((src, i) => (
                    <div key={i} className="import-multi-thumb">
                      <img src={src} alt={`Aufnahme ${i + 1}`} />
                      <button type="button" className="import-multi-thumb-remove" onClick={() => removeFile(i)} title="Entfernen">×</button>
                      <div className="import-multi-thumb-num">{i + 1}</div>
                    </div>
                  ))}
                </div>
              )}

              {error && <div className="import-error">{error}</div>}

              {files.length > 0 && (
                <button
                  className="btn btn-primary import-btn-full"
                  onClick={handleFotoScan}
                  disabled={running}
                >
                  {running
                    ? (runningProgress || <><IconLoader size={14} /> Wird gescannt…</>)
                    : `Rezept erkennen${files.length > 1 ? ` (${files.length} Aufnahmen)` : ''} →`}
                </button>
              )}

              {running && (
                <p className="import-running-hint">🤖 KI analysiert deine Bilder · DSGVO-konform · EU-Server</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── PHASE 3: Rechte & Titelbild ── */}
      {phase === 3 && (recipe || recipeData) && (
        <div className="import-phase-wrap">
          <button className="import-back-btn" onClick={goBack}><IconBack size={14} /> Zurück</button>

          {/* Erkanntes Rezept Vorschau */}
          {(() => { const d = recipe || recipeData; return (
          <div className="import-recipe-preview">
            <div className="import-recipe-preview-icon"><IconCheck size={20} /></div>
            <div>
              <div className="import-recipe-preview-title">{d.title}</div>
              <div className="import-recipe-preview-meta">
                {d.category && <span>{d.category}</span>}
                {d.servings > 0 && <span>· {d.servings} Portionen</span>}
                {d.ingredients?.length > 0 && <span>· {d.ingredients.length} Zutaten</span>}
              </div>
            </div>
          </div>
          ); })()}

          {/* Quelle */}
          <div className="import-rights-section">
            <div className="import-section-label">Wo kommt das Rezept her?</div>
            <input
              className="field-input"
              type="text"
              placeholder={method === 'url' ? 'z.B. chefkoch.de' : 'z.B. Omas Rezeptbuch, eigenes Rezept…'}
              value={source}
              onChange={e => setSource(e.target.value)}
            />
            <div className="field-hint">Optional — hilft dir später beim Wiederfinden der Quelle.</div>
          </div>

          {/* Titelbild */}
          <div className="import-rights-section">
            <div className="import-section-label">Titelbild hinzufügen <span className="import-optional">(optional)</span></div>

            {!imageMode && (
              <div className="import-image-choice">
                <button
                  className={`import-image-choice-btn${imageMode === 'file' ? ' active' : ''}`}
                  onClick={() => setImageMode('file')}
                >
                  <IconUploadAction size={16} />
                  Datei / Handy-Foto
                </button>
                <button
                  className={`import-image-choice-btn${imageMode === 'url' ? ' active' : ''}`}
                  onClick={() => setImageMode('url')}
                >
                  <IconLinkAction size={16} />
                  Bild-URL
                </button>
              </div>
            )}

            {imageMode === 'file' && (
              <div className="import-image-file-wrap">
                {!imageFile ? (
                  <label className="import-image-upload-label">
                    <input
                      type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) { setImageFile(f); setImageFilePreview(URL.createObjectURL(f)); }
                        e.target.value = '';
                      }}
                    />
                    <IconUploadAction size={20} />
                    Bild auswählen
                  </label>
                ) : (
                  <div className="import-image-file-preview">
                    <img src={imageFilePreview} alt="" />
                    <button onClick={() => { setImageFile(null); setImageFilePreview(null); }}>× Entfernen</button>
                  </div>
                )}
                <button className="import-image-choice-change" onClick={() => { setImageMode(null); setImageFile(null); setImageFilePreview(null); }}>
                  Andere Methode wählen
                </button>
              </div>
            )}

            {imageMode === 'url' && (
              <div className="import-image-url-wrap">
                {coverUrl && (
                  <div className="import-cover-preview">
                    <img
                      src={coverUrl}
                      alt="Vorschau"
                      onLoad={() => setCoverPreviewOk(true)}
                      onError={() => setCoverPreviewOk(false)}
                      style={{ display: coverPreviewOk ? 'block' : 'none' }}
                    />
                    {!coverPreviewOk && (
                      <div className="import-cover-preview-placeholder">Bild wird geladen…</div>
                    )}
                  </div>
                )}
                <input
                  className="field-input"
                  type="url"
                  placeholder="https://img.chefkoch.de/..."
                  value={coverUrl}
                  onChange={e => { setCoverUrl(e.target.value); setCoverPreviewOk(false); }}
                />
                <label className="import-rights-check">
                  <input
                    type="checkbox"
                    checked={imageRights}
                    onChange={e => setImageRights(e.target.checked)}
                  />
                  <span>Ich bin Urheber dieses Bildes oder habe das Recht es zu verwenden.</span>
                </label>
                <button className="import-image-choice-change" onClick={() => { setImageMode(null); setCoverUrl(''); setImageRights(false); setCoverPreviewOk(false); }}>
                  Andere Methode wählen
                </button>
              </div>
            )}
          </div>

          {/* Rechte-Bestätigung */}
          <div className="import-rights-section import-rights-confirm">
            <label className="import-rights-check import-rights-check--main">
              <input
                type="checkbox"
                checked={sourceRights}
                onChange={e => setSourceRights(e.target.checked)}
              />
              <span>
                Ich bestätige: Dieses Rezept gehört mir, oder ich habe das ausdrückliche Recht es hier zu speichern.
                Fremde Rezepte von Websites dürfen ohne explizite Genehmigung nicht importiert werden.
              </span>
            </label>
          </div>

          {phase3Error && <div className="import-error">{phase3Error}</div>}

          <button
            className="btn btn-primary import-btn-full"
            onClick={handlePhase3Continue}
            disabled={!sourceRights || phase3Saving}
          >
            {phase3Saving ? 'Wird gespeichert…' : 'Weiter zum Editor →'}
          </button>
        </div>
      )}

      {/* ── PHASE 4: Editor ── */}
      {phase === 4 && (recipe || recipeData) && (
        <div className="import-editor-wrap">
          <div className="import-editor-banner">
            <span>✓ Rezept importiert — bitte prüfen und speichern</span>
            <div className="import-editor-banner-actions">
              {recipe?.id && (
                <Link to={`/recipe/${recipe.id}`} className="import-editor-link">Zur Detailseite →</Link>
              )}
              <button className="import-discard-btn" onClick={handleDiscard}>Verwerfen</button>
            </div>
          </div>
          {recipe ? (
            <RecipeEditor
              key={recipe.id}
              recipeId={String(recipe.id)}
              onSaved={handleSaved}
            />
          ) : (
            <RecipeEditor
              key="new-import"
              initialData={recipeData}
              onSaved={handleSaved}
            />
          )}
        </div>
      )}
    </div>
  );
}
